<script lang="ts">
  import ProjectHeroSplit from '$lib/components/ProjectHeroSplit.svelte';
  import ProjectNav from '$lib/components/ProjectNav.svelte';
  import Footer from '$lib/components/Footer.svelte';
  import { onMount } from 'svelte';
  import { setupProjectAnimations } from '$lib/utils/animations';

  onMount(() => {
    setupProjectAnimations();
  });
</script>

<ProjectHeroSplit
  title="Teaching"
  client="FH Potsdam & UdK Berlin"
  industry="Teaching"
  product="A collection of courses and workshops exploring critical design thinking, smart objects, and visual systems."
  heroMedia={{
    type: 'image',
    src: '/projects/itto/design-fiction.png'
  }}
  metrics={[
    { value: 40, label: "Students" },
    { value: 3, label: "Institutions" },
    { value: 8, label: "Courses" }
  ]}
  roles="Course director, teaching assistant, and curriculum design for critical design thinking."
/>

<div class="project-section">
  <div class="project-grid">
    <div class="project-column-wide">
      <p class="large-text mb-8">
        A collection of teaching engagements focused on critical design thinking, smart objects, and visual systems at leading German design institutions.
      </p>
    </div>
  </div>
</div>

<div class="project-section bg-gray-50">
  <div class="project-grid">
    <div class="project-column">
      <h2 class="mb-8">Is This Thing On?</h2>
      <p class="text-sm text-gray-500">
        FH Potsdam<br>
        Interface Design Department<br>
        Winter Semester 2011/2012
      </p>
    </div>
    <div class="project-column-wide">
      <div class="space-y-6">
        <p>
          A semester-long course exploring smart objects and context awareness through critical design thinking. Students designed and developed sensor-equipped objects that respond to their environment, moving beyond conventional products.
        </p>

        <div class="grid grid-cols-2 gap-8 my-12">
          <div class="bg-gray-100">
            <img 
              src="/projects/itto/IMG_7539.JPG" 
              alt="Installation view" 
              class="w-full aspect-4/3 object-contain"
            />
          </div>
          <div class="bg-gray-100">
            <img 
              src="/projects/itto/IMG_7548.JPG" 
              alt="Project documentation" 
              class="w-full aspect-4/3 object-contain"
            />
          </div>
        </div>

        <div class="space-y-12 mt-12">
          <div>
            <h3 class="text-xl mb-4">Words of a Middle Man</h3>
            <p class="mb-4">By Christopher Steinlehrer and Jeremias Volker</p>
            <div class="bg-gray-100">
              <img 
                src="/projects/itto/itto-words-of-a-middle-man.jpg" 
                alt="Words of a Middle Man interface" 
                class="w-full aspect-16/9 object-contain mb-6"
              />
            </div>
            <p>
              A WiFi router that analyzes network communications and displays them on an integrated screen. It translates machine communication between all network participants into natural language, making it readable and understandable for humans.
            </p>
          </div>

          <div>
            <h3 class="text-xl mb-4">Installation Views</h3>
            <div class="grid grid-cols-2 gap-8">
              <div class="bg-gray-100">
                <img 
                  src="/projects/itto/installation1.jpg" 
                  alt="Installation view 1" 
                  class="w-full aspect-4/3 object-contain"
                />
              </div>
              <div class="bg-gray-100">
                <img 
                  src="/projects/itto/installation6.jpg" 
                  alt="Installation view 2" 
                  class="w-full aspect-4/3 object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div class="project-section">
  <div class="project-grid">
    <div class="project-column">
      <h2 class="mb-8">Berlin Mitte</h2>
      <p class="text-sm text-gray-500">
        UdK Berlin<br>
        Visual Systems Class<br>
        Prof. David Skopec<br>
        Summer Semester 2016
      </p>
    </div>
    <div class="project-column-wide">
      <div class="space-y-6">
        <p>
          The semester project "Mitte Berlin" in the Visual Systems class focused on Berlin's geographical center. This seemingly unremarkable green strip in a residential area of Kreuzberg served as a starting point for research, analysis, and transformation.
        </p>

        <div class="grid grid-cols-2 gap-8 my-12">
          <figure>
            <div class="bg-gray-100">
              <img 
                src="/projects/itto/berlin-mitte-1.jpeg" 
                alt="Systematic pattern studies" 
                class="w-full aspect-4/3 object-contain"
              />
            </div>
            <figcaption class="mt-2 text-sm text-gray-500">Systematic visual studies and patterns</figcaption>
          </figure>
          <figure>
            <div class="bg-gray-100">
              <img 
                src="/projects/itto/berlin-mitte-2.jpeg" 
                alt="Student work examples" 
                class="w-full aspect-4/3 object-contain"
              />
            </div>
            <figcaption class="mt-2 text-sm text-gray-500">Collection of student works exploring urban patterns</figcaption>
          </figure>
        </div>

        <p>
          Students developed methodologies for analyzing and representing urban space through systematic visual approaches. The project combined cartographic methods, demographic data, and visual analysis to create comprehensive studies of Berlin's center.
        </p>

        <div class="mt-12">
          <h3 class="text-xl mb-4">Project Structure</h3>
          <ul class="list-none space-y-2 text-sm">
            <li>3 Foreword</li>
            <li>6 Iconicity</li>
            <li>8 Cartography</li>
            <li>10 Usage</li>
            <li>12 Demographics</li>
            <li>14 Photo Analysis</li>
            <li>16 History</li>
            <li>20 Mythos Mitte</li>
            <li>24 Anthropological Collection</li>
            <li>28 Center Berlin</li>
            <li>32 Déjà vu</li>
            <li>36 mittenmang</li>
            <li>40 10×10m</li>
            <li>44 The (Un)Seen</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</div>

<ProjectNav 
  prevProject={{
    title: "The Gestalt of Digital Identity",
    url: "/projects/gestalt-digital-identity"
  }}
  nextProject={{
    title: "Framing Art Science Technology",
    url: "/projects/framing-art-science-technology"
  }}
/>

<Footer />

<style>
  .aspect-4\/3 {
    aspect-ratio: 4/3;
  }
  
  .aspect-16\/9 {
    aspect-ratio: 16/9;
  }
</style> 